import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Flame } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { SCHRIFTZUG } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const ID_FIELDS = { products: "product_id", sponsors: "sponsor_id", friends: "post_id", press: "press_id", voices: "voice_id" };

const CONFIGS = {
  products: {
    fields: [
      { name: "kind", label: "Typ", type: "select", options: ["ticket", "merch"] },
      { name: "name_de", label: "Name (DE)" },
      { name: "name_en", label: "Name (EN)" },
      { name: "desc_de", label: "Beschreibung (DE)", type: "textarea" },
      { name: "desc_en", label: "Beschreibung (EN)", type: "textarea" },
      { name: "price", label: "Preis (EUR)", type: "number" },
      { name: "lookup_key", label: "Stripe Lookup Key" },
      { name: "image", label: "Bild-URL" },
      { name: "badge_de", label: "Badge (DE)" },
      { name: "badge_en", label: "Badge (EN)" },
      { name: "sort", label: "Sortierung", type: "number" },
      { name: "active", label: "Aktiv", type: "switch" },
    ],
    display: (item) => item.name_de,
  },
  sponsors: {
    fields: [
      { name: "name", label: "Name" },
      { name: "tier", label: "Kategorie", type: "select", options: ["hoellenfuerst", "feuertitan", "kettenmeister", "funkenfreund"] },
      { name: "url", label: "Website-URL" },
      { name: "sort", label: "Sortierung", type: "number" },
      { name: "active", label: "Aktiv", type: "switch" },
    ],
    display: (item) => item.name,
  },
  friends: {
    fields: [
      { name: "author", label: "Autor:in" },
      { name: "handle", label: "Handle" },
      { name: "platform", label: "Plattform", type: "select", options: ["instagram", "youtube", "tiktok"] },
      { name: "text", label: "Text", type: "textarea" },
      { name: "image", label: "Bild-URL" },
      { name: "link", label: "Link" },
      { name: "active", label: "Aktiv", type: "switch" },
    ],
    display: (item) => `${item.author} (${item.handle})`,
  },
  press: {
    fields: [
      { name: "title_de", label: "Titel (DE)" },
      { name: "title_en", label: "Titel (EN)" },
      { name: "description_de", label: "Beschreibung (DE)", type: "textarea" },
      { name: "description_en", label: "Beschreibung (EN)", type: "textarea" },
      { name: "type", label: "Typ", type: "select", options: ["text", "image", "video"] },
      { name: "file_url", label: "Datei-/Archiv-URL" },
      { name: "sort", label: "Sortierung", type: "number" },
      { name: "active", label: "Aktiv", type: "switch" },
    ],
    display: (item) => item.title_de,
  },
  voices: {
    fields: [
      { name: "quote_de", label: "Zitat (DE)", type: "textarea" },
      { name: "quote_en", label: "Zitat (EN)", type: "textarea" },
      { name: "author", label: "Name, Ort" },
      { name: "sort", label: "Sortierung", type: "number" },
      { name: "active", label: "Aktiv", type: "switch" },
    ],
    display: (item) => `${item.quote_de?.slice(0, 60)}… — ${item.author}`,
  },
};

function ResourceManager({ resource }) {
  const { t } = useLang();
  const cfg = CONFIGS[resource];
  const idField = ID_FIELDS[resource];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [resource]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const blank = {};
    cfg.fields.forEach((f) => {
      blank[f.name] = f.type === "switch" ? true : f.type === "number" ? 0 : f.type === "select" ? f.options[0] : "";
    });
    setEditing(blank);
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      if (editing[idField]) {
        await api.put(`/admin/${resource}/${editing[idField]}`, editing);
      } else {
        await api.post(`/admin/${resource}`, editing);
      }
      toast.success(t("admin_saved"));
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/admin/${resource}/${item[idField]}`);
      toast.success(t("admin_deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-hell-smoke text-sm">{items.length} Einträge</p>
        <Button data-testid={`admin-${resource}-add`} onClick={openNew}
          className="rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.12em] uppercase text-[11px] px-5">
          <Plus className="w-4 h-4 mr-2" />{t("admin_add")}
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item[idField]} data-testid={`admin-${resource}-item-${item[idField]}`}
            className="flex items-center justify-between gap-4 border border-white/10 bg-hell-coal px-5 py-4">
            <div className="min-w-0">
              <p className="text-hell-bone truncate">{cfg.display(item)}</p>
              <p className="text-xs text-hell-smoke">
                {item.active === false ? "inaktiv" : "aktiv"}
                {item.price != null && ` · € ${Number(item.price).toFixed(2)}`}
                {item.tier && ` · ${item.tier}`}
                {item.platform && ` · ${item.platform}`}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button data-testid={`admin-${resource}-edit-${item[idField]}`} size="sm" variant="outline"
                onClick={() => { setEditing({ ...item }); setDialogOpen(true); }}
                className="border-white/20 text-hell-bone/80 hover:text-hell-gold">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button data-testid={`admin-${resource}-delete-${item[idField]}`} size="sm" variant="outline"
                onClick={() => remove(item)}
                className="border-white/20 text-hell-bone/80 hover:text-hell-redhot">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-hell-coal border-white/15 text-hell-bone max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing?.[idField] ? t("admin_edit") : t("admin_add")}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {cfg.fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="block text-xs text-hell-smoke mb-1.5">{f.label}</label>
                  {f.type === "textarea" ? (
                    <Textarea data-testid={`form-${f.name}`} rows={3} value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      className="bg-white/5 border-white/15 text-hell-bone" />
                  ) : f.type === "select" ? (
                    <Select value={editing[f.name]} onValueChange={(v) => setEditing({ ...editing, [f.name]: v })}>
                      <SelectTrigger data-testid={`form-${f.name}`} className="bg-white/5 border-white/15 text-hell-bone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-hell-coal border-white/15 text-hell-bone">
                        {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : f.type === "switch" ? (
                    <Switch data-testid={`form-${f.name}`} checked={!!editing[f.name]}
                      onCheckedChange={(v) => setEditing({ ...editing, [f.name]: v })} />
                  ) : (
                    <Input data-testid={`form-${f.name}`} type={f.type === "number" ? "number" : "text"}
                      step="0.01" value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })}
                      className="bg-white/5 border-white/15 text-hell-bone" />
                  )}
                </div>
              ))}
            </div>
          )}
          <Button data-testid="admin-form-save" onClick={save}
            className="mt-6 rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.12em] uppercase text-[11px] px-8">
            {t("admin_save")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReadOnlyList({ resource, render }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch(() => {});
  }, [resource]);
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-hell-smoke text-sm">Noch keine Einträge.</p>}
      {items.map((item, i) => (
        <div key={i} className="border border-white/10 bg-hell-coal px-5 py-4 text-sm text-hell-bone/85">
          {render(item)}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  if (!user) return null;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-hell-black px-5 md:px-10 py-10">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <img src={SCHRIFTZUG} alt="Krampus Show" className="h-8 w-auto" />
            <div>
              <h1 className="font-display text-2xl text-hell-bone">{t("admin_dashboard")}</h1>
              <p className="text-xs text-hell-smoke">{user.email}</p>
            </div>
          </div>
          <Button data-testid="admin-logout" variant="outline" onClick={async () => { await logout(); navigate("/admin"); }}
            className="border-white/20 text-hell-bone/80 hover:text-hell-redhot rounded-full">
            <LogOut className="w-4 h-4 mr-2" />{t("admin_logout")}
          </Button>
        </div>

        <Tabs defaultValue="products">
          <TabsList data-testid="admin-tabs" className="bg-hell-coal border border-white/10 flex-wrap h-auto gap-1 p-1 mb-8">
            <TabsTrigger data-testid="tab-products" value="products">{t("admin_products")}</TabsTrigger>
            <TabsTrigger data-testid="tab-sponsors" value="sponsors">{t("admin_sponsors")}</TabsTrigger>
            <TabsTrigger data-testid="tab-friends" value="friends">{t("admin_friends")}</TabsTrigger>
            <TabsTrigger data-testid="tab-press" value="press">{t("admin_press")}</TabsTrigger>
            <TabsTrigger data-testid="tab-voices" value="voices">{t("admin_voices")}</TabsTrigger>
            <TabsTrigger data-testid="tab-newsletter" value="newsletter">{t("admin_newsletter")}</TabsTrigger>
            <TabsTrigger data-testid="tab-applications" value="applications">{t("admin_applications")}</TabsTrigger>
            <TabsTrigger data-testid="tab-orders" value="orders">{t("admin_orders")}</TabsTrigger>
          </TabsList>
          <TabsContent value="products"><ResourceManager resource="products" /></TabsContent>
          <TabsContent value="sponsors"><ResourceManager resource="sponsors" /></TabsContent>
          <TabsContent value="friends"><ResourceManager resource="friends" /></TabsContent>
          <TabsContent value="press"><ResourceManager resource="press" /></TabsContent>
          <TabsContent value="voices"><ResourceManager resource="voices" /></TabsContent>
          <TabsContent value="newsletter">
            <ReadOnlyList resource="newsletter" render={(s) => (
              <span><Flame className="w-3.5 h-3.5 inline mr-2 text-hell-ember" />{s.email} · {s.lang} · {new Date(s.created_at).toLocaleDateString("de-AT")}</span>
            )} />
          </TabsContent>
          <TabsContent value="applications">
            <ReadOnlyList resource="applications" render={(a) => (
              <span><strong>{a.company}</strong> · {a.contact} · {a.email} · <span className="text-hell-gold">{a.tier}</span>{a.message ? ` — ${a.message}` : ""}</span>
            )} />
          </TabsContent>
          <TabsContent value="orders">
            <ReadOnlyList resource="orders" render={(o) => (
              <span>{o.product_name || o.lookup_key} · € {(o.amount / 100).toFixed(2)} · <span className={o.payment_status === "paid" ? "text-hell-gold" : "text-hell-smoke"}>{o.payment_status}</span> · {new Date(o.created_at).toLocaleString("de-AT")}</span>
            )} />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
